import {
  Divider,
  Form,
  PrimaryButton,
  Space,
  TertiaryButton,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

const NAME_MAX_LENGTH = 200

export interface CreatePOCustomFieldFormSchema {
  name: string
}

export interface ICreatePOCustomFieldFormProps {
  onSubmit: (values: CreatePOCustomFieldFormSchema) => void
  onCancel: () => void
  isSaving: boolean
}

export const CreatePOCustomFieldForm = ({
  onSubmit,
  onCancel,
  isSaving,
}: ICreatePOCustomFieldFormProps) => {
  const intl = useProcurifyIntl()

  const formik = useFormik<CreatePOCustomFieldFormSchema>({
    initialValues: { name: '' },
    validationSchema: Yup.object({
      name: Yup.string()
        .required(intl.formatMessage(messages.labelCaptionRequiredError))
        .max(
          NAME_MAX_LENGTH,
          intl.formatMessage(messages.labelCaptionMaxLengthError)
        ),
    }),
    onSubmit: (values, { setSubmitting }) => {
      onSubmit(values)
      // The mutation's isSaving is the in-flight indicator; guard failures
      // keep the form open, so Formik must not latch isSubmitting
      setSubmitting(false)
    },
  })

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <Form.TextField
            id='name'
            name='name'
            label={intl.formatMessage(messages.labelCaptionLabel)}
            placeholder={intl.formatMessage(messages.labelCaptionPlaceholder)}
            required
            autoFocus
            inputProps={{ maxLength: NAME_MAX_LENGTH }}
          />

          <Divider />
          <Space direction='horizontal' justify='end'>
            <TertiaryButton type='button' onClick={onCancel}>
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' isLoading={isSaving}>
              {intl.formatMessage(messages.addButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

CreatePOCustomFieldForm.displayName = 'CreatePOCustomFieldForm'
